import { SuspendableResource } from 'useSuspendableResource';
import memoizeOne from 'memoize-one';
import { convertRawProgressToContentProgressResponse } from './utils/convertGetAllProgressResponse';
import { type useContentProgressResponse } from './ContentProgress.types';

// This cache is never explicitly cleared on logout. That's okay because:
// 1. Polling timers/focus listeners are managed by useSuspendableResource's useEffect cleanup,
//    so they stop when components unmount (which happens on logout).
// 2. Stale entries are inert (no active timers) and keyed by userId, so a new user gets a fresh resource.
// 3. Full page reloads (which happen on logout) clear module-level state entirely.
const resourceCache = new Map<string, SuspendableResource<useContentProgressResponse>>();

export function getProgressSuspendableResource(userId: string): SuspendableResource<useContentProgressResponse> {
    if (!resourceCache.has(userId)) {
        // memoizeOne avoids re-creating the converted response on polling when the underlying
        // data hasn't changed. ensureUserProgressFetchedNative's buildResponse uses memoizeOne
        // internally, so it returns the same object reference when IndexedDB data is unchanged.
        // Scoped per-user so that multi-user scenarios (e.g., admin impersonation) don't
        // invalidate each other's memoization.
        const convertResponse = memoizeOne(convertRawProgressToContentProgressResponse);
        const resource = new SuspendableResource(async () => {
            // Lazy import to avoid pulling the heavy frontRoyalStoreApi → IndexedDB chain into
            // the ContentProgress barrel at module-evaluation time. That chain breaks Chromatic
            // stories that transitively import from ContentProgress.
            const { default: ensureUserProgressFetchedNative } =
                await import('StoredProgress/ensureUserProgressFetchedNative');
            const rawResponse = await ensureUserProgressFetchedNative(userId);
            return convertResponse(rawResponse);
        });
        resourceCache.set(userId, resource);
    }
    return resourceCache.get(userId)!;
}
